import React from 'react';

/**
 * MoneyInput — typed-direct currency field (no spinner arrows).
 *
 * Why a custom component instead of `<input type="number" />`:
 *   - `type="number"` shows up/down spinner arrows in Chrome / Safari that
 *     confuse restaurant owners (they think they have to click 10+ times to
 *     reach 10.00).
 *   - It also lets browsers auto-validate in weird ways and treat "0" as
 *     a sticky default that's painful to delete on tablets.
 *   - We want a free-form text field that ONLY accepts digits + one dot,
 *     auto-selects the existing value on focus (so typing replaces it),
 *     and emits a numeric value on blur.
 *
 * Props mirror a controlled <input>:
 *   - value:    current value (string OR number — both work; '' clears it)
 *   - onChange: receives the raw string during typing (so user can be mid-
 *               edit). The committed numeric value lands on blur.
 *   - onCommit: optional callback invoked on blur with a finite number or '',
 *               useful when you want to ONLY update state once the user has
 *               finished typing. Falls back to onChange if not provided.
 *
 * The "$" prefix is always rendered. To turn it off (or use a different
 * prefix), pass `prefix=""` or `prefix="+$"` etc.
 */
const MoneyInput = React.forwardRef(function MoneyInput(
  {
    value,
    onChange,
    onCommit,
    prefix = '$',
    placeholder = '0.00',
    className = '',
    inputClassName = '',
    testid,
    disabled = false,
    min = 0,
    max,
    ...rest
  },
  ref,
) {
  const displayValue = value === '' || value == null
    ? ''
    : (typeof value === 'number' ? String(value) : value);

  const handleChange = (e) => {
    // Accept digits and at most one dot. Strip everything else so paste
    // events with commas or letters degrade gracefully.
    let raw = e.target.value.replace(/[^0-9.]/g, '');
    const firstDot = raw.indexOf('.');
    if (firstDot !== -1) {
      raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
    }
    onChange?.(raw, e);
  };

  const handleBlur = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '.') {
      if (onCommit) onCommit('', e);
      return;
    }
    let n = parseFloat(raw);
    if (!Number.isFinite(n)) {
      if (onCommit) onCommit('', e);
      return;
    }
    if (n < min) n = min;
    if (max != null && n > max) n = max;
    // Round to 2 decimal places — currency, never need more precision.
    const rounded = Number(n.toFixed(2));
    if (onCommit) onCommit(rounded, e);
    else onChange?.(String(rounded), e);
  };

  return (
    <div className={`relative ${className}`}>
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500 pointer-events-none">
          {prefix}
        </span>
      )}
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        className={`${prefix ? 'pl-7' : ''} ${inputClassName}`}
        {...rest}
      />
    </div>
  );
});

export default MoneyInput;
